import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { FactCheck as FactCheckIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { formatDateTime } from '../../services/expenseVouchersApi';
import { operationsApi, SEVERITY_META } from '../../services/expenseOperationsApi';

const headerCell = { color: 'white', fontWeight: 600, whiteSpace: 'nowrap' };

// Expense launch readiness (HR admin / Accounts). Read-only: lists the configuration and data
// problems the server found, and whether each one blocks launch. READY only when nothing does.
const ExpenseLaunchReadiness = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [severity, setSeverity] = useState('');
  const [check, setCheck] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData((await operationsApi.readiness()).data);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const issues = useMemo(() => (data?.issues || [])
    .filter((i) => !severity || i.severity === severity)
    .filter((i) => !check || i.check === check), [data, severity, check]);

  const ready = data?.status === 'READY';

  return (
    <Box sx={{ bgcolor: 'background.default', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, fontSize: '30px', lineHeight: 1.5, letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
          <FactCheckIcon color="primary" />
          Expense Launch Readiness
        </Typography>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Re-run check</Button>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Configuration and data problems that would stop Expense submission, approval, booking or payment. Nothing is changed by this check.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}
      {loading && !data && <CircularProgress />}

      {data && (
        <>
          <Alert severity={ready ? 'success' : 'error'} sx={{ mb: 3 }} data-testid="readiness-status">
            <AlertTitle sx={{ fontWeight: 700, fontSize: 20 }}>{ready ? 'READY' : 'NOT READY'}</AlertTitle>
            {ready
              ? `No blocking problems. ${data.summary.warnings} warning(s) and ${data.summary.info} note(s) to review.`
              : `${data.summary.blockers} blocking problem(s) must be fixed before launch.`}
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              Checked {formatDateTime(data.generatedAt)} · {data.summary.expenseEmployees} expense-enabled employee(s) · {data.summary.accountsUsers} Accounts user(s)
            </Typography>
            {!ready && (
              <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }} data-testid="blocking-reasons">
                {data.blockingReasons.map((r) => <li key={r}><Typography variant="body2">{r}</Typography></li>)}
              </Box>
            )}
          </Alert>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }} data-testid="readiness-checks">
            {data.checks.map((c) => (
              <Chip
                key={c.key}
                label={`${c.label}: ${c.passed ? 'OK' : c.issues}`}
                color={c.passed ? 'success' : c.blockers ? 'error' : 'warning'}
                variant={check === c.key ? 'filled' : 'outlined'}
                onClick={() => setCheck(check === c.key ? '' : c.key)}
              />
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
            <TextField select size="small" label="Severity" value={severity} onChange={(e) => setSeverity(e.target.value)} sx={{ minWidth: 160 }}>
              <MenuItem value="">All</MenuItem>
              {Object.entries(SEVERITY_META).map(([k, m]) => <MenuItem key={k} value={k}>{m.label}</MenuItem>)}
            </TextField>
            <Typography variant="body2" color="text.secondary">{issues.length} issue(s) shown</Typography>
          </Box>

          <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
            <Table size="small" data-testid="readiness-issues">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                  {['Severity', 'Blocks launch', 'Check', 'Employee / vendor / configuration', 'Problem', 'Suggested action'].map((h) => <TableCell key={h} sx={headerCell}>{h}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {issues.length === 0 && (
                  <TableRow><TableCell colSpan={6}><Typography color="text.secondary">No issues.</Typography></TableCell></TableRow>
                )}
                {issues.map((i, idx) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={`${i.check}-${i.subjectId || i.subject}-${idx}`}>
                    <TableCell><Chip size="small" label={SEVERITY_META[i.severity].label} color={SEVERITY_META[i.severity].color} /></TableCell>
                    <TableCell>{i.blocksLaunch ? <strong>Yes</strong> : 'No'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{i.checkLabel}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{i.subject}</Typography>
                      {i.subjectDetail && <Typography variant="caption" color="text.secondary">{i.subjectDetail}</Typography>}
                    </TableCell>
                    <TableCell>{i.problem}</TableCell>
                    <TableCell>{i.suggestedAction}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default ExpenseLaunchReadiness;
